import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import {
  IFtthOrchestrator,
  IModemReturnOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { IFtthReactivationOrchestrator } from "@nju/data-access-portal-orchestrator";
import { IPayment } from "@nju/data-access-portal-payment";
import {
  IAccess,
  IModem,
  IServiceStatus,
  useProducts,
} from "@nju/data-access-portal-product";
import { IInfoResponse } from "@nju/data-access-portal-user";
import { portalRoutes } from "@nju/routes";
import { GeneralErrorNotification, Panel, Separator, Text } from "@nju/ui";
import { Buttons } from "./Buttons";
import { ftthServiceCTABannerMachine } from "./FtthServiceCTABannerMachine";
import { getFtthServiceCTABannerConditions } from "./getFtthServiceCTABannerConditions";
import { getFtthStatusData } from "./getFtthStatusData";

interface IServiceCTABannerProps {
  access: IAccess | undefined;
  anniversaryDate: string | undefined;
  hasNoProducts: boolean;
  payment: IPayment | undefined;
  modemProducts: IModem | undefined;
  modemReturnOrchestrator: IModemReturnOrchestrator | undefined;
  orchestrator: IFtthOrchestrator | undefined;
  reactivationOrchestratorStatus:
    | IFtthReactivationOrchestrator["state"]
    | undefined;
  serviceStatus: IServiceStatus["ftthServiceStatus"];
  user: IInfoResponse;
}

export function FtthServiceCTABanner({
  access,
  anniversaryDate,
  hasNoProducts,
  payment,
  modemProducts,
  modemReturnOrchestrator,
  orchestrator,
  reactivationOrchestratorStatus,
  serviceStatus,
  user,
}: IServiceCTABannerProps) {
  const { push, reload } = useRouter();
  const { ftthProducts } = useProducts();
  const { commitment } = ftthProducts;
  const isLoyaltyOffer = !!commitment;
  const willBreakLoyaltyOnResignation =
    !!commitment?.willBreakLoyaltyOnResignation;
  const penaltyFee = commitment?.penaltyFee;

  const [current, send] = useMachine(ftthServiceCTABannerMachine, {
    context: {
      ...ftthServiceCTABannerMachine.context,
      payerId: orchestrator?.userId,
      productId: access?.id,
      currentStatus: serviceStatus,
      modemReturnOrchestrator,
      hasNoProducts,
      ftthOrchestratorState: orchestrator?.state,
    },
    actions: {
      onSubscribeSuccess: () => {
        reload();
      },
      onRetryPaymentSuccess: () => {
        reload();
      },
    },
  });

  const isLoading =
    current.matches("subscribe") || current.matches("retryPayment");
  const hasUnpaidInvoices = current.context.invoices?.length > 0;

  const subscribe = React.useCallback(
    (status: IServiceStatus["ftthServiceStatus"]) => {
      send({ type: "SUBSCRIBE", currentStatus: status });
    },
    [send],
  );

  const retryPayment = React.useCallback(() => {
    send({ type: "RETRY_PAYMENT" });
  }, [send]);

  const refresh = React.useCallback(() => {
    reload();
  }, [reload]);

  const conditions = React.useMemo(() => {
    return getFtthServiceCTABannerConditions({
      payment,
      orchestrator,
      serviceStatus,
      reactivationOrchestratorStatus,
      hasNoProducts,
      modemReturnOrchestrator,
      hasUnpaidInvoices,
      user,
    });
  }, [
    payment,
    orchestrator,
    serviceStatus,
    reactivationOrchestratorStatus,
    hasNoProducts,
    modemReturnOrchestrator,
    hasUnpaidInvoices,
    user,
  ]);

  const panelProps = React.useMemo(() => {
    return getFtthStatusData({
      access,
      anniversaryDate,
      conditions,
      isLoyaltyOffer,
      modemProducts,
      modemReturnOrchestrator,
      orchestrator,
      penaltyFee,
      willBreakLoyaltyOnResignation,
    });
  }, [
    conditions,
    anniversaryDate,
    access,
    isLoyaltyOffer,
    modemProducts,
    orchestrator,
    modemReturnOrchestrator,
    willBreakLoyaltyOnResignation,
    penaltyFee,
  ]);

  if (!panelProps) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  const { text, title, iconName } = panelProps;

  if (current.matches("error")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          push(portalRoutes.mojeKonto.index.get({ mode: "ftth" }));
        }}
      />
    );
  }

  return (
    <>
      <Panel
        iconName={iconName}
        isLoading={isLoading}
        testId="ftth-service-status-banner"
        title={title}
      >
        <Separator size="xs" tone="invisible" />
        <Text
          size={{
            "@onlyMobile": "m",
            "@fromTablet": "l",
          }}
        >
          {text}
        </Text>
        <Separator size="xs" tone="invisible" />
        <Buttons
          access={access}
          anniversaryDate={anniversaryDate}
          conditions={conditions}
          ftthOrchestrator={orchestrator}
          ftthServiceStatus={serviceStatus}
          modemProducts={modemProducts}
          modemReturnOrchestrator={modemReturnOrchestrator}
          payment={payment}
          refreshCallback={refresh}
          retryPaymentCallback={retryPayment}
          subscribeCallback={subscribe}
        />
      </Panel>
      <Separator size="xs" tone="invisible" />
    </>
  );
}
